import React, { useRef, useState } from 'react';
import coupleImage from '../../../assets/images/couples/Vector.png';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1 mobile, 2 otp, 3 password
  const [mobile, setMobile] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const navigate = useNavigate();
  const otpRefs = useRef([]);

  const apiBase = process.env.REACT_APP_API_BASE_URL;

  const validateMobile = (value) => /^[6-9]\d{9}$/.test(value);

  const otpValue = () => otpDigits.join('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!validateMobile(mobile)) {
      setError('Enter a valid 10-digit mobile number starting with 6-9.');
      return;
    }

    setIsSubmit(true);
    try {
      const payload = new FormData();
      payload.append('mobile', mobile);
      const res = await axios.post(`${apiBase}Register_controller/forgot_password_send_otp`, payload);
      if (res.data.response === 'success') {
        setInfo(res.data.message || 'OTP sent to your mobile.');
        setStep(2);
        setOtpDigits(['', '', '', '']);
      } else {
        setError(res.data.message || 'Could not send OTP.');
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmit(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otpDigits];
    next[index] = value.slice(-1);
    setOtpDigits(next);
    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    const otp = otpValue();
    if (otp.length !== 4) {
      setError('Enter the 4-digit OTP.');
      return;
    }

    setIsSubmit(true);
    try {
      const payload = new FormData();
      payload.append('mobile', mobile);
      payload.append('otp', otp);
      const res = await axios.post(`${apiBase}Register_controller/forgot_password_verify_otp`, payload);
      if (res.data.response === 'success') {
        setInfo(res.data.message || 'OTP verified.');
        setStep(3);
      } else {
        setError(res.data.message || 'Invalid OTP.');
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmit(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Password and confirm password do not match.');
      return;
    }

    setIsSubmit(true);
    try {
      const payload = new FormData();
      payload.append('mobile', mobile);
      payload.append('otp', otpValue());
      payload.append('password', password);
      payload.append('confirm_password', confirmPassword);
      const res = await axios.post(`${apiBase}Register_controller/forgot_password_reset`, payload);
      if (res.data.response === 'success') {
        setInfo(res.data.message || 'Password updated successfully.');
        setTimeout(() => navigate('/login/'), 1800);
      } else {
        setError(res.data.message || 'Could not reset password.');
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmit(false);
    }
  };

  const resendOtp = async () => {
    setError('');
    setInfo('');
    setIsSubmit(true);
    try {
      const payload = new FormData();
      payload.append('mobile', mobile);
      const res = await axios.post(`${apiBase}Register_controller/forgot_password_send_otp`, payload);
      if (res.data.response === 'success') {
        setInfo('OTP resent to your mobile.');
        setOtpDigits(['', '', '', '']);
      } else {
        setError(res.data.message || 'Could not resend OTP.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <>
      <div className="py-md-5"></div>
      <div className="forgot-wrapper">
        <div className="forgot-container">
          <div className="forgot-left">
            <h2 className="forgot-title">Reset Password</h2>
            <p className="forgot-subtitle">
              {step === 1 && 'Enter your registered mobile number to receive OTP.'}
              {step === 2 && `Enter the 4-digit OTP sent to +91 ${mobile}.`}
              {step === 3 && 'Create your new password.'}
            </p>

            <div className="forgot-steps">
              <span className={step >= 1 ? 'active' : ''}>1. Mobile</span>
              <span className={step >= 2 ? 'active' : ''}>2. OTP</span>
              <span className={step >= 3 ? 'active' : ''}>3. Password</span>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {info && <div className="alert alert-success">{info}</div>}

            {step === 1 && (
              <form onSubmit={handleSendOtp} className="forgot-form">
                <label>
                  Mobile Number <span className="req">*</span>
                </label>
                <input
                  type="tel"
                  className="email-input"
                  value={mobile}
                  maxLength={10}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter registered mobile"
                  required
                />
                <button type="submit" className="forgot-btn" disabled={isSubmit}>
                  {isSubmit ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleVerifyOtp} className="forgot-form">
                <label>
                  Enter OTP <span className="req">*</span>
                </label>
                <div className="otp-row">
                  {otpDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (otpRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      className="otp-box"
                    />
                  ))}
                </div>
                <button type="submit" className="forgot-btn" disabled={isSubmit}>
                  {isSubmit ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button type="button" className="forgot-link-btn" onClick={resendOtp} disabled={isSubmit}>
                  Resend OTP
                </button>
                <button type="button" className="forgot-link-btn" onClick={() => { setStep(1); setError(''); setInfo(''); }}>
                  Change mobile number
                </button>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={handleResetPassword} className="forgot-form">
                <label>
                  New Password <span className="req">*</span>
                </label>
                <div className="password-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                  />
                  <button type="button" className="eye-btn" onClick={() => setShowPassword((v) => !v)}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <label>
                  Confirm Password <span className="req">*</span>
                </label>
                <div className="password-wrap">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    required
                  />
                  <button type="button" className="eye-btn" onClick={() => setShowConfirm((v) => !v)}>
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <button type="submit" className="forgot-btn" disabled={isSubmit}>
                  {isSubmit ? 'Updating...' : 'Reset Password'}
                </button>
              </form>
            )}

            <p className="remember-link">
              Remembered your password? <Link to="/login/">Log in</Link>
            </p>
          </div>

          <div className="forgot-right d-none d-lg-block">
            <img src={coupleImage} alt="Scenery" className="forgot-image" />
            <div className="image-overlay">
              <h2>
                Your Journey To Love Begins <span className="highlight">Here !</span>
              </h2>
              <p className="text-light">
                Connect With Genuine Matches Who <br />
                Share Your <span className="highlight">Values</span> And <span className="highlight">Dreams</span>.
              </p>
            </div>
          </div>

          <style>{`
.req { color: red; }
.forgot-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 670px;
  background-color: #fafafa;
  padding: 16px;
}
.forgot-container {
  display: flex;
  width: 90%;
  max-width: 1000px;
  min-height: 600px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}
.forgot-left {
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.forgot-title {
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: center;
}
.forgot-subtitle {
  color: #666;
  margin-bottom: 18px;
  text-align: center;
}
.forgot-steps {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 18px;
  font-size: 12px;
  color: #999;
}
.forgot-steps .active {
  color: #e53d5c;
  font-weight: 600;
}
.forgot-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.forgot-form input {
  padding: 12px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  width: 100%;
}
.otp-row {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin: 8px 0 12px;
}
.otp-box {
  width: 48px !important;
  height: 48px;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
}
.password-wrap {
  position: relative;
}
.eye-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
}
.forgot-btn {
  background-color: #e53d5c;
  color: white;
  padding: 12px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 8px;
}
.forgot-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.forgot-link-btn {
  background: transparent;
  border: none;
  color: #e53d5c;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 0;
}
.remember-link {
  margin-top: 20px;
  font-size: 0.9rem;
  color: #666;
  text-align: center;
}
.remember-link a {
  color: #e53d5c;
  text-decoration: none;
  font-weight: 500;
}
.forgot-right {
  flex: 1;
  position: relative;
  overflow: hidden;
}
.forgot-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.image-overlay {
  position: absolute;
  bottom: 40px;
  left: 20px;
  color: white;
  padding: 20px;
}
.highlight { color: #ffc107; }
@media (max-width: 768px) {
  .forgot-container {
    flex-direction: column;
    height: auto;
  }
  .forgot-left { padding: 24px; }
}
`}</style>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
